import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { MatRadioChange } from '@angular/material/radio';
import { EPersona, ESkeletonType } from '@norfolk-southern/accessns-components';

import { NSThirdPartyConstants } from '@ruby/configs/third-party.constants';
import { ICustomer, ILocation } from '@ruby/modules/customer-select/models/customer.interface';
import { UtilsService } from '@ruby/shared/services/utils/utils.service';

@Component({
  selector: 'ng-third-party-locations',
  templateUrl: './third-party-locations.dialog.html',
  styleUrls: ['./third-party-locations.dialog.scss']
})
export class ThirdPartyLocationsDialogComponent implements OnChanges {
  @Input() customers!: Array<ICustomer>;
  @Input() role!: EPersona;
  @Input() editCustomerLoc?: ICustomer;
  @Input() loading!: boolean;
  @Input() isEdit!: boolean;
  @Output() selectedLocations = new EventEmitter<ICustomer>();
  skeleton: ESkeletonType = ESkeletonType.LIST;
  selectedCustomer?: ICustomer;
  allComplete = false;

  constructor(private utilsSvc: UtilsService) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.editCustomerLoc && changes.editCustomerLoc.currentValue) {
      this.selectedCustomer = this.utilsSvc.deepCopy(changes.editCustomerLoc.currentValue);
    }

    if (this.role === EPersona.SL && this.selectedCustomer && this.customers.length > 0) {
      this.allComplete = this.selectedCustomer.locations.length === this.customers.length;
    }

    if (changes.role && changes.role.currentValue === EPersona.SL && !this.selectedCustomer) {
      this.selectedCustomer = NSThirdPartyConstants.defaultCustomerForSL;
    }

    if (changes.customers && changes.customers.currentValue.length > 0) {
      const customer = this.findCustomer(this.customers);
      if (customer) {
        if (this.isEdit && this.selectedCustomer) {
          const tempCustomer = this.utilsSvc.deepCopy(customer);
          tempCustomer.locations = this.selectedCustomer.locations;
          this.selectedCustomer = tempCustomer;
          this.allComplete = this.selectedCustomer!.locations.length === customer.locations.length;
        }
      }
    }
  }

  /**
   * Select
   *
   * @summary The following method selects a customer with locations
   * @param $event: MatRadioChange
   * @returns void
   */
  select($event: MatRadioChange): void {
    this.selectedCustomer = { ...this.utilsSvc.deepCopy($event.value) };
    this.allComplete = true;
  }

  /**
   * IsIndeterminate
   *
   * @summary The following method controls the animation of the Select All checkbox
   * @returns boolean
   */
  isIndeterminate(): boolean {
    return this.selectedCustomer ? (!this.allComplete && this.selectedCustomer.locations.length > 0) : false;
  }

  /**
   * Select All
   *
   * @summary The following method selects all the locations of a selected customer
   * @param checked: boolean
   * @param locations: Array<ILocation>
   * @returns void
   */
  selectAll(checked: boolean, locations: Array<ILocation>): void {
    if (this.selectedCustomer) {
      this.selectedCustomer.locations = checked ? [...locations] : [];
      this.allComplete = locations.length === this.selectedCustomer.locations.length;
    }
  }

  /**
   * Scac Checked
   *
   * @summary The following method checks if scac is checked or not
   * @param scac?: string
   * @returns boolean
   */
  scacChecked(scac?: string): boolean {
    return this.selectedCustomer ? !!this.selectedCustomer.locations.find((loc: ILocation) => loc.stationName === scac) : false;
  }

  /**
   * Select All Scacs
   *
   * @summary The following method add/remove all scacs, this method uses the entire customers array instead of locations property
   * @param checked: boolean
   * @returns void
   */
  selectAllScacs(checked: boolean): void {
    if (this.selectedCustomer) {
      this.selectedCustomer.locations = checked ? this.customers.map((customer: ICustomer) => ({
        scac: customer.scac ? customer.scac : '',
        stationName: customer.scac ? customer.scac : ''
      }) as ILocation) : [];
      this.allComplete = this.customers.length === this.selectedCustomer.locations.length;
    }
  }

  /**
   * Select Scac
   *
   * @summary The following method add/remove scacs
   * @param newScac?: string
   * @returns void
   */
  selectScac(newScac?: string): void {
    if (this.selectedCustomer) {
      const found = this.selectedCustomer.locations.find((loc: ILocation) => loc.stationName === newScac);
      if (!found) {
        this.selectedCustomer.locations.push({
          stationName: newScac ? newScac : '',
          scac: newScac!,
          id: '',
          locationName: '',
          classCode: '',
          controlLocationId: 0,
          divisionName: '',
          divisionNumber: '',
          locationIdentifier: '',
          stationCode: '',
          tenLevelId: '',
          tranLocationId: 0,
          customerId: 0
        });
      } else {
        this.selectedCustomer.locations = this.selectedCustomer.locations.filter((loc: ILocation) => loc.stationName !== newScac);
      }
      this.allComplete = this.customers.length === this.selectedCustomer.locations.length;
    }
  }

  /**
   * Update Selected Locations
   *
   * @summary The following method add/remove the locations
   * @param loc: ILocation
   * @param locSize: number
   * @returns void
   */
  updateSelectedLocations(loc: ILocation, locSize: number): void {
    if (this.selectedCustomer) {
      const indexFound = this.selectedCustomer.locations
        .findIndex((location: ILocation) => this.locationsEqual(location, loc));
      if (indexFound === -1) {
        this.selectedCustomer.locations.push(loc);
      } else {
        this.selectedCustomer.locations.splice(indexFound, 1);
      }
      this.allComplete = locSize === this.selectedCustomer.locations.length;
    }
  }

  /**
   * Emit Locations
   *
   * @summary The following method emits an event with the selected customer an its locations
   * @returns void
   */
  emitLocations(): void {
    this.selectedLocations.emit(this.selectedCustomer);
  }

  /**
   * Parse Locations
   *
   * @summary The following method parse the name by Line Of Business
   * @returns void
   */
  parseLocations(loc: ILocation, scac?: string): string {
    let location = loc.locationName;
    if (this.role === EPersona.SL && scac) {
      location = scac;
    } else {
      if (loc.classCode) {
        location += `(${ loc.classCode })`;
      }
    }
    return location;
  }

  /**
   * Location Exist
   *
   * @summary The following method checks if the selected location exist in the selected customer object
   * @param loc: ILocation
   * @returns boolean
   */
  locationExist(loc: ILocation): boolean {
    if (this.selectedCustomer) {
      return !!this.selectedCustomer.locations.find((cLoc: ILocation) => this.locationsEqual(cLoc, loc));
    }
    return false;
  }

  private findCustomer(customers: Array<ICustomer>): ICustomer | undefined {
    return customers.find((customer: ICustomer) =>
      customer.locations.find((customerLocations: ILocation) =>
        this.selectedCustomer ? this.selectedCustomer.locations.find((loc: ILocation) => this.locationsEqual(loc, customerLocations)) : false)
    );
  }

  private locationsEqual(src: ILocation, loc: ILocation): boolean {
    if (src.classCode) {
      return !!(src.stationName && src.stationName.split(',')[0].includes(loc.locationName.split(',')[0]) && src.classCode === loc.classCode);
    }
    if (src.scac && loc.scac) {
      return !!(src.stationName && src.stationName.split(',')[0].includes(loc.locationName.split(',')[0]) && src.scac === loc.scac);
    }
    if (src.locationName && loc.locationName) {
      return !!(src.locationName.split(',')[0].includes(loc.locationName.split(',')[0]));
    }
    return !!(src.stationName && src.stationName.split(',')[0].includes(loc.locationName.split(',')[0]));
  }
}
